/**
 * Human-friendly wording for social sign-in problems.
 *
 * Providers (Apple in particular) return terse, technical strings such as
 * "access_denied" or "user_cancelled_authorize". We translate them into plain
 * language and separate the two cases that need different UI:
 *
 *  - "cancelled": the person backed out on purpose. Calm, no alarm.
 *  - "failed":    something actually went wrong. Offer a retry.
 */
export type OAuthProvider = "google" | "apple";

export type OAuthFailure = {
  kind: "cancelled" | "failed";
  /** Short headline, safe to show on its own. */
  title: string;
  /** One sentence of guidance. */
  message: string;
  /** Whether a "Try again" affordance makes sense. */
  retryable: boolean;
};

export function providerLabel(provider: OAuthProvider): string {
  return provider === "apple" ? "Apple" : "Google";
}

const CANCEL_PATTERNS = [
  /access_denied/i,
  /user_cancell?ed/i,
  /cancell?ed/i,
  /user_denied/i,
  /consent_required/i,
  /popup[_ ]?closed/i,
  /closed by (the )?user/i,
  /aborted/i,
];

const POPUP_BLOCKED = /popup.*(block|open)|window\.open/i;
const NETWORK = /network|failed to fetch|timeout|timed out|offline/i;
const CONFIG = /not supported|unsupported provider|not enabled|invalid_client|configuration/i;

/** Map anything a provider or the auth wrapper throws into copy we can show. */
export function describeOAuthError(provider: OAuthProvider, raw?: string | null): OAuthFailure {
  const label = providerLabel(provider);
  const text = (raw ?? "").trim();

  if (text && CANCEL_PATTERNS.some((re) => re.test(text))) {
    return {
      kind: "cancelled",
      title: `${label} sign-in was cancelled`,
      message: `No changes were made. You can try ${label} again or continue with your email address.`,
      retryable: true,
    };
  }

  if (POPUP_BLOCKED.test(text)) {
    return {
      kind: "failed",
      title: `${label} couldn't open`,
      message: "Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.",
      retryable: true,
    };
  }

  if (NETWORK.test(text)) {
    return {
      kind: "failed",
      title: "Connection problem",
      message: `We couldn't reach ${label}. Check your connection and try again.`,
      retryable: true,
    };
  }

  if (CONFIG.test(text)) {
    return {
      kind: "failed",
      title: `${label} sign-in is unavailable`,
      message: `${label} sign-in isn't available right now. Please continue with your email address instead.`,
      retryable: false,
    };
  }

  return {
    kind: "failed",
    title: `We couldn't finish ${label} sign-in`,
    message: `Something went wrong on the way back from ${label}. Please try again.`,
    retryable: true,
  };
}

/**
 * Providers redirect back with the failure in the query string or the URL
 * fragment. Read it once, then strip those params so a refresh doesn't
 * resurrect a stale banner.
 */
export function consumeOAuthErrorFromUrl(): OAuthFailure | null {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
  const pick = (key: string) => url.searchParams.get(key) ?? hash.get(key);

  const error = pick("error");
  const code = pick("error_code");
  const description = pick("error_description");
  if (!error && !code && !description) return null;

  const providerHint = (pick("provider") ?? "").toLowerCase();
  const provider: OAuthProvider = providerHint === "apple" ? "apple" : providerHint === "google" ? "google" : "apple";

  const raw = [error, code, description].filter(Boolean).join(" ");
  const failure = describeOAuthError(provider, decodeURIComponent(raw));

  for (const key of ["error", "error_code", "error_description", "provider", "state"]) {
    url.searchParams.delete(key);
    hash.delete(key);
  }
  const remainingHash = hash.toString();
  url.hash = remainingHash ? `#${remainingHash}` : "";
  window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);

  return failure;
}
